import type { ApolloError } from '@apollo/client'

import type { Channel, CommentData, CommentsQuery } from '~publish/gql/graphql'

import type { BaseCommentsFilters } from '../../CommentsFilters/useFilters'
import { useCommentsQuery } from '../../hooks/useCommentsQuery'
import { useCommentsScore } from '../../hooks/useCommentsScore'
import { useEngagementTimer } from './useEngagementTimer'
import { useCommentScoreTracker } from './useCommentScoreTracker'
import { useScoreSessionSync } from './useScoreSessionSync'

export type UseCommentsDataProps = {
  channel: Channel
  filters?: BaseCommentsFilters
}

export type CommentsData = {
  query: {
    data?: CommentsQuery
    loading: boolean
    error?: ApolloError | null
    fetchingMore: boolean
    lastElementRef: (node: HTMLElement | null) => void
  }
  comments: {
    list: CommentData[]
    hasComments: boolean
  }
}

export const useCommentsData = ({
  channel,
  filters,
}: UseCommentsDataProps): CommentsData => {
  // Query comments data
  const { data, loading, error, fetchingMore, lastElementRef } =
    useCommentsQuery({
      channelIds: [channel.id],
      filters,
    })

  const hasComments = (data?.comments?.edges?.length ?? 0) > 0
  const comments =
    data?.comments?.edges?.map((edge) => edge.node as CommentData) ?? []

  return {
    query: {
      data,
      loading,
      error,
      fetchingMore,
      lastElementRef,
    },
    comments: {
      list: comments,
      hasComments,
    },
  }
}

export type UseCommentsEngagementDataProps = {
  channelId: string
}

export type CommentsEngagementData = {
  score: {
    current: number | null
    delta: number | null
    hasIncreased: boolean
  }
  engagement: {
    isActivelyEngaging: boolean
    activeTime: string
    repliedToManyComments: boolean
  }
}

export const useCommentsEngagementData = ({
  channelId,
}: UseCommentsEngagementDataProps): CommentsEngagementData => {
  // Comments score data
  const { commentsScore, loading: isScoreLoading } = useCommentsScore({
    channelId,
  })

  // Score tracking
  const currentScore = commentsScore?.currentWeekScore.totalValue ?? null
  const { hasScoreIncreased, scoreDelta, startSession, stopSession } =
    useCommentScoreTracker(
      !isScoreLoading && currentScore != null ? currentScore : null,
      channelId,
    )

  // Engagement timer
  const { isActivelyEngaging, activeTime } = useEngagementTimer()

  const repliedToManyComments = false // TODO: temporary until we build this state

  // Handle score session management: for has score increased, we need to start a session
  useScoreSessionSync(channelId, startSession, stopSession)

  return {
    score: {
      current: currentScore,
      delta: scoreDelta,
      hasIncreased: !isScoreLoading && hasScoreIncreased,
    },
    engagement: {
      isActivelyEngaging,
      activeTime,
      repliedToManyComments,
    },
  }
}

// Legacy type and hook for backward compatibility
export type UseCommentsListDataProps = UseCommentsDataProps

export type CommentsListData = CommentsData & CommentsEngagementData

export const useCommentsListData = ({
  channel,
  filters,
}: UseCommentsListDataProps): CommentsListData => {
  const commentsData = useCommentsData({ channel, filters })
  const engagementData = useCommentsEngagementData({ channelId: channel.id })

  return {
    ...commentsData,
    ...engagementData,
  }
}
